import { useCallback, useContext, useEffect, useState } from "react";
import { ChatbotDataContext } from "../context/ChatbotDataContext.js";
import { WebSocketManager } from "../utils/WebSocketManager.js";
function useRealTimeWebSocket({ url, locale = 'en-us', realtimeContext, onMessage, analytics, pennysid, requestFrom, isCustomVoice = false }) {
    const [isConnected, setIsConnected] = useState(false);
    const { chatbotFeatures } = useContext(ChatbotDataContext);
    const shouldUseAgentic = chatbotFeatures?.configs?.pennyEnableRealtimeAgentic ?? false;
    const wsManager = WebSocketManager.getInstance();
    const connectionKey = `${url}-${locale}`;
    const getWebSocketUrl = useCallback((baseUrl)=>{
        if ('development' === process.env.NODE_ENV) switch(window.location.hostname){
            case 'localhost':
                return 'ws://0.0.0.0:8080';
            case 'local.priceline.com':
                return 'wss://local.priceline.com';
            default:
                return baseUrl;
        }
        return baseUrl.replace(/^http:/, 'ws:').replace(/^https:/, 'wss:');
    }, []);
    useEffect(()=>{
        const wsUrl = getWebSocketUrl(url);
        const fullUrl = `${wsUrl}/genai-svc/genai/realtime/chat?locale=${locale}&pennysid=${pennysid}&requestFrom=${requestFrom}&isCustomVoice=${isCustomVoice}&useRealtimeAgent=${shouldUseAgentic ? 'y' : 'n'}`;
        const ws = wsManager.createConnection(connectionKey, fullUrl);
        const handleOpen = ()=>{
            setIsConnected(true);
            const pingInterval = setInterval(()=>{
                if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({
                    type: 'realtime_ping'
                }));
            }, 30000);
            return pingInterval;
        };
        const handleClose = (event)=>{
            setIsConnected(false);
            if (1005 !== event.code) analytics.logError({
                loggedFrom: 'useRealTimeWebSocket.onclose',
                message: `WebSocket closed - Code: ${event.code}, Reason: ${event.reason}`,
                error: event
            });
        };
        const handleError = (error)=>{
            analytics.logError({
                loggedFrom: 'useRealTimeWebSocket.onerror',
                message: 'WebSocket encountered an error',
                error
            });
            setIsConnected(false);
        };
        let pingInterval = null;
        if (ws.readyState === WebSocket.CONNECTING) {
            ws.onopen = ()=>{
                pingInterval = handleOpen();
            };
            ws.onclose = handleClose;
            ws.onerror = handleError;
        } else if (ws.readyState === WebSocket.OPEN) pingInterval = handleOpen();
        wsManager.addMessageListener(connectionKey, onMessage);
        return ()=>{
            wsManager.removeMessageListener(connectionKey, onMessage);
            if (pingInterval) clearInterval(pingInterval);
            wsManager.closeConnection(connectionKey);
        };
    }, [
        url,
        locale,
        realtimeContext,
        onMessage,
        analytics,
        getWebSocketUrl,
        connectionKey
    ]);
    const sendMessage = useCallback((message)=>{
        const ws = wsManager.getConnection(connectionKey);
        if (!ws || ws.readyState !== WebSocket.OPEN) return void console.warn('WebSocket is not connected');
        const messageStr = 'string' == typeof message ? message : JSON.stringify(message);
        ws.send(messageStr);
    }, [
        connectionKey
    ]);
    return {
        isConnected,
        sendMessage
    };
}
export { useRealTimeWebSocket };
